import { addHours, format, subHours } from 'date-fns';
import * as R from 'ramda';
import { TimeConstraints } from 'react-datetime';
import { v4 as uuid } from 'uuid';

import { SelectOptionType } from '@rebound/design-system/components/Select';
import { CollectionDate } from '@rebound/shared-models/carrier/carrier.model';
import {
  DraftReturn,
  DraftReturnItem,
  DraftReturnItemAction,
} from '@rebound/shared-models/draft-return/draft-return.model';
import { StoreCreditsForItem } from '@rebound/shared-models/exchange/exchange.model';
import { Item, Order } from '@rebound/shared-models/order/order.model';
import {
  CycleonRetailerFeaturesEnum,
  NestedReturnReasonOption,
  Retailer,
  SubReasonOption,
  UserJourneyEnum,
} from '@rebound/shared-models/retailer/retailer.model';
import { Return } from '@rebound/shared-models/return/return.model';

import { dateSatisfiesReturnPolicy } from '@modules/dashboard/dashboard.utils';

import {
  CarrierInformationFormValues,
  DraftReturnCarrierFormData,
  PersonalInformationFormValues,
  ReturnJourneyStepSlug,
} from './return-journey.model';

export const getStepsConfig = (
  stepsToSkip: ReturnJourneyStepSlug[],
): string[] => {
  let stepsList = [
    'createReturnPage.step1Name',
    'createReturnPage.step2Name',
    'createReturnPage.step3Name',
    'createReturnPage.step4Name',
    'createReturnPage.step5Name',
  ];

  if (!stepsToSkip.length) {
    return stepsList;
  }

  if (stepsToSkip.includes(ReturnJourneyStepSlug.SelectItems)) {
    stepsList = stepsList.filter(
      (stepName) => stepName !== 'createReturnPage.step1Name',
    );
  }

  if (stepsToSkip.includes(ReturnJourneyStepSlug.PersonalInformation)) {
    stepsList = stepsList.filter(
      (stepName) => stepName !== 'createReturnPage.step2Name',
    );
  }

  if (stepsToSkip.includes(ReturnJourneyStepSlug.PayForDelivery)) {
    stepsList = stepsList.filter(
      (stepName) => stepName !== 'createReturnPage.step4Name',
    );
  }

  return stepsList;
};

export const getStepSlugs = (retailer?: Retailer): string[] => {
  if (
    retailer?.userJourneyRadio === UserJourneyEnum.RETURN_WITHOUT_SALES_ORDER
  ) {
    return stepSlugs.map((slug) => {
      if (slug === ReturnJourneyStepSlug.PersonalInformation) {
        return `${slug}-step`;
      }
      if (slug === ReturnJourneyStepSlug.SelectCarrier) {
        return `${slug}-step`;
      }
      return slug;
    });
  }

  return stepSlugs;
};

export const stepSlugs = [
  ReturnJourneyStepSlug.SelectItems,
  ReturnJourneyStepSlug.PersonalInformation,
  ReturnJourneyStepSlug.SelectCarrier,
  ReturnJourneyStepSlug.PayForDelivery,
  ReturnJourneyStepSlug.Summary,
];

export const getStepSlugConfiguredList = (
  stepsToSkip: ReturnJourneyStepSlug[],
): ReturnJourneyStepSlug[] =>
  stepSlugs.filter((stepSlug) => !stepsToSkip.includes(stepSlug));

export const getStepSlugNumber = (
  currentStepSlug: string,
  stepsToSkip: ReturnJourneyStepSlug[],
): number =>
  R.indexOf(currentStepSlug, getStepSlugConfiguredList(stepsToSkip)) + 1;

export const optionsForOptionSelect: SelectOptionType[] = [
  { value: 'Refund', id: uuid() },
  { value: 'Exchange to store credits', id: uuid() },
  { value: 'Exchange to another item', id: uuid() },
];

export const returnItemFormDefaultValues = {
  quantity: 1,
  returnOption: DraftReturnItemAction.Refund,
  reasons: [
    {
      action: DraftReturnItemAction.Refund,
      reason: '',
      wish: '',
      comment: '',
      code: '',
      itemToExchange: null,
      storeCreditsToExchange: null,
    },
  ],
};

export const getReturnReason = (
  action: DraftReturnItemAction,
  reason = '',
  reasonCategory = '',
  comment = '',
) => ({
  action,
  reason,
  reasonCategory,
  wish: '',
  comment,
  code: '',
  itemToExchange: null,
  storeCreditsToExchange: null,
});

export const personalInformationDefaultValues: PersonalInformationFormValues = {
  name: '',
  email: '',
  phoneNumber: '',
  country: '',
  county: '',
  postCode: '',
  city: '',
  state: '',
  address1: '',
  address2: '',
};

export const carrierInformationDefaultValues: CarrierInformationFormValues = {
  address1: '',
  phoneNumber: '',
  cycleonRmaNumber: '',
  county: '',
  companyName: '',
  approvedMessage: false,
};

export const checkPersonalFieldsChanged = (
  draftReturn: DraftReturn | undefined,
  formValues: PersonalInformationFormValues,
) => {
  if (
    draftReturn?.name !== formValues.name ||
    draftReturn?.email !== formValues.email ||
    draftReturn?.phoneNumber !== formValues.phoneNumber ||
    draftReturn?.country !== formValues.country ||
    draftReturn?.county !== formValues.county ||
    draftReturn?.postCode !== formValues.postCode ||
    draftReturn?.city !== formValues.city ||
    draftReturn?.state !== formValues.state ||
    draftReturn?.address1 !== formValues.address1 ||
    draftReturn?.address2 !== formValues.address2
  ) {
    return true;
  }

  return false;
};

export const checkPickUpFieldsChanged = (
  draftReturn: DraftReturn | undefined,
  formValues: CarrierInformationFormValues,
) =>
  draftReturn?.phoneNumber !== formValues.phoneNumber ||
  draftReturn?.address1 !== formValues.address1 ||
  draftReturn?.cycleonRmaNumber !== formValues.cycleonRmaNumber ||
  draftReturn?.county !== formValues.county ||
  draftReturn?.companyName !== formValues.companyName;

export const checkIsStateNecessary = (countryValue: string) =>
  countryValue.startsWith('AU') ||
  countryValue.startsWith('US') ||
  countryValue.startsWith('CA');

export const getTimeRangeFromDate = (date: Date, range: number) => {
  const startRangeDate = subHours(date, range);
  const endRangeDate = addHours(date, range);

  return `${format(startRangeDate, 'hh:mm')} - ${format(
    endRangeDate,
    'hh:mm aa',
  )}`;
};

export const findEarliestDate = (
  dates: CollectionDate[] | boolean,
): Date | null => {
  if (Array.isArray(dates) && dates.length > 0) {
    const sortedDates = [...dates].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
    return new Date(sortedDates[0].date);
  }
  return null;
};

export const getMatchingCollectionDate = (
  inputDate: Date | null,
  dates: CollectionDate[] | boolean,
): CollectionDate | null => {
  if (inputDate && Array.isArray(dates)) {
    const inputDateFormatted = inputDate.toISOString().split('T')[0];

    const matchingDate = dates.find((dateObj) => {
      const dateObjFormatted = new Date(dateObj.date)
        .toISOString()
        .split('T')[0];
      return dateObjFormatted === inputDateFormatted;
    });

    return matchingDate ?? null;
  }
  return null;
};

export const isDateInCollection = (
  targetDate: Date,
  collectionDates: CollectionDate[] | boolean,
): boolean => {
  if (Array.isArray(collectionDates)) {
    const targetDateFormatted = targetDate.toISOString().split('T')[0];

    return collectionDates.some((dateObj) => {
      const dateObjFormatted = new Date(dateObj.date)
        .toISOString()
        .split('T')[0];
      return dateObjFormatted === targetDateFormatted;
    });
  }
  return false;
};

export const getTimeConstraints = (
  timeFrom: string,
  timeTo: string,
): TimeConstraints => ({
  hours: {
    min: parseInt(timeFrom.split(':')[0]) + 1,
    max: parseInt(timeTo.split(':')[0]) - 1,
    step: 1,
  },
  minutes: {
    min: 0,
    max: 59,
    step: 10,
  },
});

export const getNextStepNumber = (
  currentStepSlug: string,
  retailer: any,
  draftReturn: DraftReturn | undefined,
) => {
  let stepSlugsFiltered = getStepSlugs(retailer);

  if (retailer && retailer.personalInfoStepDisabled) {
    stepSlugsFiltered = stepSlugs.filter(
      (slug) => slug !== ReturnJourneyStepSlug.PersonalInformation,
    );
  }

  if (
    !paymentFeatureEnabled(retailer) ||
    (draftReturn && draftReturn.isCarrierServiceFree)
  ) {
    stepSlugsFiltered = stepSlugsFiltered.filter(
      (slug) => slug !== ReturnJourneyStepSlug.PayForDelivery,
    );
  }

  return R.indexOf(currentStepSlug, stepSlugsFiltered) + 2;
};

export const paymentFeatureEnabled = (retailer: Retailer | undefined) =>
  retailer &&
  retailer.portal &&
  retailer.portal.enabledFeatures &&
  retailer.portal.enabledFeatures.includes(CycleonRetailerFeaturesEnum.PAYMENT);

export const deductFromRefundEnabled = (retailer: Retailer | undefined) =>
  retailer &&
  retailer.portal &&
  retailer.portal.enabledFeatures &&
  retailer.portal.enabledFeatures.includes(
    CycleonRetailerFeaturesEnum.DEDUCT_FROM_REFUND,
  );

export const isItemWithinReturnWindow = (
  item: Pick<Item, 'deliveryDate'>,
  order: Pick<Order, 'orderDate' | 'clientOrderCreatedDate'>,
  returnPolicyDays: number,
) => {
  const startDate =
    item.deliveryDate ?? order.clientOrderCreatedDate ?? order.orderDate;

  return dateSatisfiesReturnPolicy(startDate, returnPolicyDays);
};

export const anyItemWithinReturnWindow = (
  order: Pick<Order, 'orderDate' | 'clientOrderCreatedDate' | 'items'>,
  returnPolicyDays: number,
) =>
  order.items.some((item) =>
    isItemWithinReturnWindow(item, order, returnPolicyDays),
  );

export const AUStatesCodes = [
  { name: 'Australian Capital Territory', code: 'ACT' },
  { name: 'New South Wales', code: 'NSW' },
  { name: 'Northern Territory', code: 'NT' },
  { name: 'Queensland', code: 'QLD' },
  { name: 'South Australia', code: 'SA' },
  { name: 'Tasmania', code: 'TAS' },
  { name: 'Victoria', code: 'VIC' },
  { name: 'Western Australia', code: 'WA' },
];

export const CAStatesCodes = [
  { name: 'Alberta', code: 'AB' },
  { name: 'British Columbia', code: 'BC' },
  { name: 'Manitoba', code: 'MB' },
  { name: 'New Brunswick', code: 'NB' },
  { name: 'Newfoundland and Labrador', code: 'NL' },
  { name: 'Northwest Territories', code: 'NT' },
  { name: 'Nova Scotia', code: 'NS' },
  { name: 'Nunavut', code: 'NU' },
  { name: 'Ontario', code: 'ON' },
  { name: 'Prince Edward Island', code: 'PE' },
  { name: 'Quebec', code: 'QC' },
  { name: 'Saskatchewan', code: 'SK' },
  { name: 'Yukon Territory', code: 'YT' },
];

export const USStatesCodes = [
  { code: 'AK', name: 'Alaska' },
  { code: 'AL', name: 'Alabama' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'GU', name: 'Guam' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'IA', name: 'Iowa' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MD', name: 'Maryland' },
  { code: 'ME', name: 'Maine' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MT', name: 'Montana' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NY', name: 'New York' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VA', name: 'Virginia' },
  { code: 'VI', name: 'Virgin Islands' },
  { code: 'VT', name: 'Vermont' },
  { code: 'WA', name: 'Washington' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WY', name: 'Wyoming' },
];

//used for translating currency names that may come from the store credits endpoint
const currencyLocaleMap: Record<string, string> = {
  USD: 'en-US',
  CAD: 'en-CA',

  EUR: 'en-IE',
  GBP: 'en-GB',
  CHF: 'de-CH',
  RON: 'ro-RO',
  SEK: 'sv-SE',
  NOK: 'nb-NO',
  DKK: 'da-DK',
  PLN: 'pl-PL',
  CZK: 'cs-CZ',
  HUF: 'hu-HU',

  JPY: 'ja-JP',
  CNY: 'zh-CN',

  BRL: 'pt-BR',
};

//used for displaying store credits symbols
const symbolCurrencyMap: Record<string, string> = {
  $: 'USD',
  '€': 'EUR',
  '£': 'GBP',
  '¥': 'JPY',

  CHF: 'CHF',
  RON: 'RON',

  kr: 'SEK',

  zł: 'PLN',
  Kč: 'CZK',
  Ft: 'HUF',

  R$: 'BRL',
};

/* Add in this array new country codes (based on iso standard) that do not use postal codes */
export const countriesWithNoPostalCode = ['IE'];

export const generateOptionsDataForCountryOrState = (
  list: {
    code: string;
    name: string;
  }[],
) =>
  list.map((item) => ({
    value: item.code,
    label: item.name,
    id: uuid(),
  }));

type StateItem = { code: string; name: string }[];

export const getStateCode = (country: string, stateName: string) => {
  const stateMap: { [key: string]: StateItem } = {
    US: USStatesCodes,
    CA: CAStatesCodes,
    AU: AUStatesCodes,
  };

  const stateList = stateMap[country.toUpperCase()] || [];

  const state =
    stateList.find(
      (item) => item.name.toUpperCase() === stateName.toUpperCase(),
    ) ||
    stateList.find(
      (item) => item.code.toUpperCase() === stateName.toUpperCase(),
    );

  return state ? state.code : '';
};

export const initialDraftReturnCarrierData: DraftReturnCarrierFormData = {
  collectionDate: null,
  collectionInterval: null,
  dropOffPointId: '',
  dropOffPointAddress: '',
  carrierServiceId: null,
  carrierServiceImgPath: '',
  carrierServiceName: '',
  carrierServiceType: '',
  postalCompanyId: null,
  isCarrierServiceFree: true,
  deliveryPrice: 0,
  deliveryCurrency: '',
};

const hasFilledReason = (item: DraftReturnItem): boolean =>
  Boolean(item.reasons?.some((r) => Boolean(r.reason)));

export const dedupeDraftReturnItems = (
  items: DraftReturnItem[],
): DraftReturnItem[] => {
  const keptIndexByCycleonId = new Map<string, number>();
  const result: DraftReturnItem[] = [];

  items.forEach((item) => {
    if (!item.cycleonId) {
      result.push(item);
      return;
    }

    const existingIndex = keptIndexByCycleonId.get(item.cycleonId);

    if (existingIndex === undefined) {
      keptIndexByCycleonId.set(item.cycleonId, result.length);
      result.push(item);
      return;
    }

    // Prefer the "most complete" entry; keep the first one otherwise.
    if (!hasFilledReason(result[existingIndex]) && hasFilledReason(item)) {
      result[existingIndex] = item;
    }
  });

  return result;
};

export const isValidSelectedItemsStep = (
  currentLanguage: string,
  retailer?: Retailer,
  items?: DraftReturnItem[],
) => {
  const returnReasons = retailer?.nestedReturnReasons;

  if (!returnReasons || returnReasons.length === 0) {
    return false;
  }
  if (!items || items.length === 0) {
    return false;
  }

  return items.every((item) =>
    item.reasons.every((itemReason) => {
      let isExchangeValid = true;

      if (itemReason.action === DraftReturnItemAction.ExchangeToCredits) {
        isExchangeValid = Boolean(itemReason.storeCreditsToExchange?.value);
      }

      if (itemReason.action === DraftReturnItemAction.ExchangeToItem) {
        isExchangeValid = Boolean(itemReason.itemToExchange);
      }

      let isCommentValid = true;

      if (!retailer.nestedReturnReasonEnabled) {
        const matchedConfigReason =
          retailer.nestedReturnReasons[0]?.subReasons.find(
            (subReason: SubReasonOption) =>
              !Array.isArray(subReason.prettyName) &&
              itemReason.reason === subReason.prettyName[currentLanguage],
          );

        if (matchedConfigReason?.isCommentRequired) {
          isCommentValid = Boolean(
            itemReason.comment && itemReason.comment.trim() !== '',
          );
        }
      } else {
        const matchedReasonCategory = retailer.nestedReturnReasons.find(
          (nestedReason: NestedReturnReasonOption) =>
            !Array.isArray(nestedReason.reasonCategoryPrettyName) &&
            itemReason.reasonCategory ===
              nestedReason.reasonCategoryPrettyName[currentLanguage],
        );

        const matchedSubReason = matchedReasonCategory?.subReasons.find(
          (subReason: SubReasonOption) =>
            !Array.isArray(subReason.prettyName) &&
            itemReason.reason === subReason.prettyName[currentLanguage],
        );

        if (matchedSubReason?.isCommentRequired) {
          isCommentValid = Boolean(
            itemReason.comment && itemReason.comment.trim() !== '',
          );
        }
      }

      return isExchangeValid && Boolean(itemReason.reason) && isCommentValid;
    }),
  );
};

export enum CarrierServiceFilterType {
  DropOff = 'drop_off',
  PickUp = 'home_collection',
  SendIn = 'send_in',
  All = 'all',
}

export const getItemCreditsValue = (
  creditsForItemData: StoreCreditsForItem | undefined,
): string => {
  if (!creditsForItemData) return '';

  const { storeCredits, unit } = creditsForItemData;
  const currency = symbolCurrencyMap[unit] ?? unit;
  const locale = currencyLocaleMap[currency];

  if (locale) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      maximumFractionDigits: 2,
    }).format(storeCredits);
  }

  return `${storeCredits}${unit}`;
};

export const downloadBase64File = (
  base64: string,
  contentType: string,
  fileName: string,
) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: contentType });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

type OrderLookupNormalized = {
  orderReference: string;
  postalCode: string;
};

const normalizeTrackItem = (
  item: Return | DraftReturn,
): OrderLookupNormalized => {
  if ('postCode' in item) {
    return {
      orderReference: item.orderReference ?? '',
      postalCode: item.postCode ?? '',
    };
  }

  return {
    orderReference: item.orderReference ?? '',
    postalCode: item.zip ?? '',
  };
};

export const storeOrderParamsAndResolveRedirect = (
  userJourney?: string,
  trackData?: Return[] | DraftReturn,
) => {
  if (!userJourney) return '/';

  let orderReference = '';
  let postalCode = '';
  let item: Return | DraftReturn | undefined;

  if (trackData) {
    if (Array.isArray(trackData) && trackData.length > 0) {
      item = trackData[0];
    } else if (!Array.isArray(trackData)) {
      item = trackData;
    }
    if (item) {
      const normalized = normalizeTrackItem(item);
      orderReference = normalized.orderReference;
      postalCode = normalized.postalCode;
    }
  }

  if (userJourney === UserJourneyEnum.ORDER_LOOKUP) {
    if (orderReference) localStorage.setItem('orderReference', orderReference);
    if (postalCode) localStorage.setItem('postalCode', postalCode);
  }
  if (userJourney === UserJourneyEnum.EMAIL_VERIFICATION) {
    const token = localStorage.getItem('token');
    return token ? `/dashboard/orders?highlightOrder=${orderReference}` : '/';
  }

  return '/search-order';
};

export const extractErrorCode = (message?: string | null): string => {
  if (!message) return 'somethingWentWrong';
  const trimmed = String(message).trim();
  if (/^[A-Z]{5}\d{3}$/.test(trimmed)) {
    return trimmed;
  }
  const bracketMatch = trimmed.match(/\[([A-Z]{5}\d{3})\]/);
  if (bracketMatch && bracketMatch[1]) {
    return bracketMatch[1];
  }
  const inlineMatch = trimmed.match(/([A-Z]{5}\d{3})/);
  if (inlineMatch && inlineMatch[1]) {
    return inlineMatch[1];
  }

  return 'somethingWentWrong';
};
